import { useCallback, useMemo } from 'react'
import { NO_STATUS_SENTINEL_ID, isClosedProgressType } from '../constants'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { useStatuses as useStatusesRxDB } from '@/lib/db/rxdb/hooks'
import { getProgressTypeOrder } from '@/lib/types/subscription'
import useLoginStore from '@/store/LoginStore'

interface StatusGroup {
  readonly statuses: Array<StatusDocument>
  readonly statusIds: ReadonlyArray<string>
  readonly hasStatuses: boolean
  readonly statusFilterKey: string
}

export function useStatuses() {
  const workspaceId = useLoginStore((state) => state.workspaceId)

  // Subscribe to statuses
  const { statuses: allStatuses, isLoading } =
    useStatusesRxDB({
      isDeleted: false,
    })

  const scopedStatuses = useMemo<Array<StatusDocument>>(() => {
    const filtered = allStatuses.filter((status) => {
      if (workspaceId && status.envId !== workspaceId) {
        return false
      }

      return true
    })
    return filtered.sort((a, b) => {
      const orderDiff =
        getProgressTypeOrder(a.progressType) -
        getProgressTypeOrder(b.progressType)
      if (orderDiff !== 0) return orderDiff
      return a.sortOrder.localeCompare(b.sortOrder)
    })
  }, [allStatuses, workspaceId])

  // TODO: The StatusDocument schema doesn't have progress_type field
  // This needs to be added to the schema to properly filter open/closed statuses
  const openStatuses = useMemo<Array<StatusDocument>>(
    () =>
      scopedStatuses.filter(
        (status) => !isClosedProgressType((status as any).progressType),
      ),
    [scopedStatuses],
  )

  const createStatusGroup = useCallback(
    (statuses: Array<StatusDocument>): StatusGroup => {
      const statusIds = statuses.map((status) => status.id)
      const hasStatuses = statusIds.length > 0
      const statusIdsForQuery = hasStatuses
        ? statusIds
        : [NO_STATUS_SENTINEL_ID]
      const statusFilterKey = statusIdsForQuery.join('|')

      return {
        statuses,
        statusIds: statusIdsForQuery,
        hasStatuses,
        statusFilterKey,
      }
    },
    [],
  )

  const allGroup = useMemo(
    () => createStatusGroup(scopedStatuses),
    [createStatusGroup, scopedStatuses],
  )
  const openGroup = useMemo(
    () => createStatusGroup(openStatuses),
    [createStatusGroup, openStatuses],
  )

  return {
    all: allGroup,
    open: openGroup,
    isLoading,
  }
}

// Legacy exports removed - use useStatuses instead
